import useOrgBaseRoles from '@/org/hooks/useOrgBaseRoles'
import { useOrgContext, useOrgEditActions } from '@/org/contexts/OrgContext'
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from '@chakra-ui/react'
import { CircleFragment } from '@gql'
import { getSeedRoles, leaderSeedRoleName } from '@rolebase/shared/seeds/roles'
import { omit } from '@utils/omit'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useCircleParentLinks from '../hooks/useCircleParentLinks'

interface Props {
  circle: CircleFragment
  parentCircle: CircleFragment
  memberId: string
  isOpen: boolean
  onClose(): void
}

// The two moments where adding a member to a sub-circle is ambiguous: it has no
// parent-link role to put them in, or it has several. One modal, because it is
// the same moment (member chosen, target unclear) with the same outcome.
export default function CircleParentLinkPickModal({
  circle,
  parentCircle,
  memberId,
  isOpen,
  onClose,
}: Props) {
  const { t } = useTranslation()
  const { orgData, orgId } = useOrgContext()
  const { createCircle, addCircleMember } = useOrgEditActions()
  const baseRoles = useOrgBaseRoles()
  const links = useCircleParentLinks(circle.id)
  const available = links.filter((link) => link.canTakeMember)

  const [selectedId, setSelectedId] = useState(available[0]?.circle.id ?? '')
  const [loading, setLoading] = useState(false)

  const memberName = orgData?.members.find((m) => m.id === memberId)?.name ?? ''
  const circleName = orgData?.getRole(circle.roleId)?.name ?? ''
  const parentName = orgData?.getRole(parentCircle.roleId)?.name ?? ''

  // Assign to the chosen link role.
  const handleConfirm = useCallback(async () => {
    if (!selectedId) return
    setLoading(true)
    try {
      await addCircleMember(selectedId, memberId)
      onClose()
    } finally {
      setLoading(false)
    }
  }, [selectedId, memberId, addCircleMember, onClose])

  // No link role: create the default Leader, then assign into it. The seed is
  // the source of the role's shape (packages/shared/seeds/roles.ts) — the ONLY
  // place a role name is matched, and only to pick which base role to create.
  // Finding a circle's existing link roles never looks at names (parentLink).
  const handleCreateLeader = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const seed = getSeedRoles(orgId).find(
        (role) => role.name === leaderSeedRoleName
      )
      if (!seed) return
      const existing = baseRoles?.find((role) => role.name === seed.name)
      const circleId = existing
        ? await createCircle(circle.id, existing)
        : await createCircle(circle.id, seed.name, omit(seed, 'orgId', 'name'))
      if (circleId) await addCircleMember(circleId, memberId)
      onClose()
    } finally {
      setLoading(false)
    }
  }, [
    orgId,
    baseRoles,
    circle.id,
    memberId,
    createCircle,
    addCircleMember,
    onClose,
  ])

  // Upstream allows a member with no role, and the cadrage keeps it.
  const handleAddWithoutRole = useCallback(async () => {
    setLoading(true)
    try {
      await addCircleMember(circle.id, memberId)
      onClose()
    } finally {
      setLoading(false)
    }
  }, [circle.id, memberId, addCircleMember, onClose])

  const hasLinks = available.length > 0

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('CircleParentLinkPickModal.title', {
            member: memberName,
            circle: circleName,
          })}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {hasLinks ? (
            <>
              <Text mb={3}>{t('CircleParentLinkPickModal.whichLink')}</Text>
              <RadioGroup value={selectedId} onChange={setSelectedId}>
                <VStack align="start" spacing={2}>
                  {available.map((link) => (
                    <Radio key={link.circle.id} value={link.circle.id}>
                      {link.role.name}
                    </Radio>
                  ))}
                </VStack>
              </RadioGroup>
            </>
          ) : (
            <Text>
              {t('CircleParentLinkPickModal.noLink', {
                parent: parentName,
                circle: circleName,
              })}
            </Text>
          )}
        </ModalBody>

        <ModalFooter>
          {hasLinks ? (
            <>
              <Button variant="ghost" mr={3} onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="blue"
                isLoading={loading}
                isDisabled={!selectedId}
                onClick={handleConfirm}
              >
                {t('CircleParentLinkPickModal.confirm')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                mr={3}
                isLoading={loading}
                onClick={handleAddWithoutRole}
              >
                {t('CircleParentLinkPickModal.addWithoutRole')}
              </Button>
              <Button
                colorScheme="blue"
                isLoading={loading}
                onClick={handleCreateLeader}
              >
                {t('CircleParentLinkPickModal.createLeader')}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
